import { ServiceCommonLayout } from '../ServiceCommonLayout/ServiceCommonLayout.component';
import { CriteriaForm } from './components/CriteriaForm/CriteriaForm.component';
import { UiTable } from './components/UiTable/UiTable.component';
import { TreatmentScheduleContext } from 'pages/les-outils/planning-de-traitement';
import React, { ComponentPropsWithRef, FC, useContext, useMemo } from 'react';
import { useSelector } from 'react-redux';
import useForecastTool, { ForecastQueries } from '@api/business/treatmentHook/useForecastTool';
import { BecomeProOverlay } from '@components/BecomeProOverlay/BecomeProOverlay.component';
import { HightTempIcon } from '@components/icons/meteo/HightTemp';
import { RainAndWindIcon } from '@components/icons/meteo/RainAndWind';
import { RainRiskIcon } from '@components/icons/meteo/RainRisk';
import { SmallHumidityIcon } from '@components/icons/meteo/SmallHumidity';
import { WindBoldIcon } from '@components/icons/meteo/WindBold';
import { WarningIcon } from '@components/icons/Warning.icon';
import { BarIcon } from '@components/LegendsBloc/BarIcon.component';
import { LegendBlock } from '@components/LegendsBloc/LegendBlock.component';
import { PlcMessageWithRetry } from '@components/PlcMessage/PlcMessageWithRetry.component';
import { useTranslation } from '@locales/useTranslation.hook';
import { useLocation } from '@meteo/business/LocationSelector.hook';
import { PlcColor } from '@styles/colors';
import { userSelectors } from '@user/business/User.store';
import style from './TreatmentSchedulePageLayout.module.scss';

type Props = ComponentPropsWithRef<'main'>;

const legendOptions = [
  [
    { icon: <BarIcon color={PlcColor.GREEN_DARK_LEAF} />, label: 'very-good' },
    { icon: <BarIcon color={PlcColor.GREEN_LEAF} />, label: 'good' },
    { icon: <BarIcon color={PlcColor.YELLOW_SUN} />, label: 'medium' },
  ],
  [
    { icon: <BarIcon color={PlcColor.NUDE_WHEAT} />, label: 'bad' },
    { icon: <BarIcon color={PlcColor.RED_PEPPER} />, label: 'very-bad' },
  ],
  [
    { icon: <WindBoldIcon />, label: 'strong-wind' },
    { icon: <RainAndWindIcon />, label: 'rain-wind' },
    { icon: <RainRiskIcon />, label: 'rain-risk' },
  ],
  [
    { icon: <HightTempIcon />, label: 'temp' },
    { icon: <SmallHumidityIcon />, label: 'humidity' },
    { icon: <WarningIcon />, label: 'multi' },
  ],
];

const TreatmentSchedulePageLayout: FC<Props> = ({ className, ...rest }) => {
  const { t } = useTranslation(['treatment-schedule']);

  const {
    city: { data: selectedCity },
    news: { data: news, isValidating: isNewsLoading },
  } = useContext(TreatmentScheduleContext);
  const { coordinates, city } = useLocation(selectedCity);

  const { isLoading, tableData, forecastQueries, setForecastQueries, getPrevision, previsionError } = useForecastTool({
    coordinates,
  });

  const isPro = useSelector(userSelectors.isPro);

  function handleOnForecastQueryChange(query: keyof ForecastQueries, min: number, max: number) {
    setForecastQueries((s) => ({
      ...s,
      [query]: {
        max,
        min,
      },
    }));
  }

  const legendOptionsWithI18n = useMemo(
    () =>
      legendOptions.map((row) =>
        row.map(({ icon, label }) => ({ icon, labelI18n: `treatment-schedule.page.dropdown.options.${label}` }))
      ),
    []
  );
  const cityInfos = city ? `${city.properties.city ?? city.properties.name}  ${city.properties.postcode}` : undefined;
  const title = `${t('treatment-schedule.page.title')}${cityInfos ? ` - ${cityInfos}` : ''}`;

  return (
    <ServiceCommonLayout
      className={style['treatment-schedule-page']}
      i18nServiceBreadcrumb="treatment-schedule.page.title"
      i18nServiceSubTitle="treatment-schedule.page.content"
      i18nServiceTitle={title}
      isNewsLoading={isNewsLoading}
      metaTitleI18n="treatment-schedule.page.title"
      news={news}
      serviceSlug="planning-de-traitement"
      {...rest}
    >
      {isPro ? (
        <>
          <section>
            <CriteriaForm
              className="plc-mb-xxl"
              forecastQueries={forecastQueries}
              getPrevision={getPrevision}
              onForecastQueryChange={handleOnForecastQueryChange}
            />
          </section>

          {previsionError ? (
            <PlcMessageWithRetry retryCallback={getPrevision} />
          ) : (
            <>
              <UiTable isLoading={Boolean(isLoading)} tableData={tableData} />
              <LegendBlock legendOptions={legendOptionsWithI18n} />
            </>
          )}
        </>
      ) : (
        <BecomeProOverlay asOverlay={false} i18nTitle="treatment-schedule.become-pro.title" />
      )}
    </ServiceCommonLayout>
  );
};

export { TreatmentSchedulePageLayout };
